import React from "react";
import { SearchPrompt, SearchQueryTier } from "../types";
import { isMonitoredDomain } from "./utils";

type Props = {
    domain: string;
    assistantLabel: string;
    niche: string;
    searchPrompts: SearchPrompt[];
}

type TierSection = {
    tier: SearchQueryTier;
    title: string;
    description: string;
}

// The three kinds of query, in the order they are shown.
const TIER_SECTIONS: TierSection[] = [
    {
        tier: 'brand',
        title: 'Brand perception queries',
        description: 'Questions that name the brand directly.'
    },
    {
        tier: 'category',
        title: 'Specific-need search queries',
        description: 'Detailed questions from people who do not know the brand yet.'
    },
    {
        tier: 'niche',
        title: 'Broad market search queries',
        description: 'General questions asked across the whole market.'
    }
];

export default function SearchPromptsList({ domain, assistantLabel, niche, searchPrompts }: Props) {
    const prompts = searchPrompts || [];

    // Analyses stored before the queries were split into tiers carry no tier at all.
    // They are shown as the single flat list they were generated as.
    const hasTiers = prompts.some(prompt => !!prompt.tier);

    const sections = hasTiers
        ? TIER_SECTIONS
            .map(section => ({ ...section, prompts: prompts.filter(prompt => prompt.tier === section.tier) }))
            .filter(section => section.prompts.length > 0)
        : [{
            tier: null,
            title: `${assistantLabel} Search Queries About: ${niche}`,
            description: `These are the questions people ask ${assistantLabel}, and the websites recommended in the answers.`,
            prompts
        }];

    let questionNumber = 0;

    return (
        <div className="search-prompts-list">
            {sections.map((section, sectionIndex) => (
                <div className="search-prompts-section" key={sectionIndex}>
                    <div className="section-title">
                        <div className="title">{section.title}</div>
                        <div className="description">{section.description}</div>
                    </div>
                    {section.prompts.map((prompt, index) => {
                        questionNumber += 1;

                        return (
                            <div className="prompt-mention-section" key={index}>
                                <div className="search-prompt">
                                    <div className="search-prompt-title"><strong>Question #{questionNumber}</strong></div>
                                    {prompt.query}
                                </div>

                                <div className="answer-container">
                                    <div style={{ lineHeight: "24px" }}><strong>{assistantLabel} answer and cited URLs</strong></div>
                                    <div className="answer">{prompt.query_response}</div>
                                    <ul className="citations-list">
                                        {prompt.results.map((result, resultIndex) => (
                                            <li key={resultIndex} className={`${isMonitoredDomain(result.domain, domain) ? "highlighted" : ""}`}>
                                                <img
                                                    className="favicon"
                                                    src={`https://www.google.com/s2/favicons?domain=${result.domain}`}
                                                    width="16"
                                                    height="16"
                                                    alt={`favicon of ${result.domain}`}
                                                    onError={(e) => {
                                                        e.currentTarget.className = 'default';
                                                        if (e.currentTarget.src !== '/images/default_favicon.png') {
                                                            e.currentTarget.src = '/images/default_favicon.png';
                                                        }
                                                    }}
                                                />
                                                {/* Domain and page title inside one link, so the underline runs under the whole
                                                    text and any part of it opens the page. */}
                                                <a href={result.url} target="_blank" rel="noopener noreferrer">
                                                    {result.domain} - <span style={{ fontStyle: "italic" }}>{result.title ? result.title : result.url}</span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}
